import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchResourceWithPost } from '@app/src/api/fetchHooks'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'

interface ExpectationAffectedCountQuery {
  providerFilters: FilterGroup[]
  automationId: number | null
  assessmentTemplateId: number | null
}

/**
 * Hook to count providers affected by expectation automation when applying to existing.
 *
 * Filters for providers with self-reported assessments on the automation's template.
 * Backend applies additional logic: risk status exceeds threshold OR assessment level below target.
 * Excludes providers who already have expectations for this automation and template.
 */
const useExpectationAffectedCount = (automationId: number | undefined, assessmentTemplateId: number | null) => {
  const providerFilters: FilterGroup[] = assessmentTemplateId
    ? [
        // Must be linked (have an organization)
        {
          name: 'linked',
          filters: [{ value: 'true', operator: Operators.EqualTo }],
        },
        // Must have a self-reported assessment for the template
        {
          name: 'assessments.isScreened',
          filters: [{ value: 'false', operator: Operators.EqualTo }],
        },
        {
          name: 'assessments.assessmentTemplateId',
          filters: [{ value: assessmentTemplateId.toString(), operator: Operators.EqualTo }],
        },
      ]
    : []

  const { data: affectedCount, isLoading: isLoadingCount } = useFetchResourceWithPost<
    number,
    ExpectationAffectedCountQuery
  >({
    key: FetchKey.ExpectationAffectedCount,
    endpoint: endpoints.expectationAutomationAffectedCount,
    body: {
      providerFilters,
      automationId: automationId ?? null,
      assessmentTemplateId,
    },
    options: {
      enabled: Boolean(assessmentTemplateId),
    },
  })

  return { affectedCount, isLoadingCount: isLoadingCount || !assessmentTemplateId }
}

export default useExpectationAffectedCount
